import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Copy, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { DepartmentManagers } from "@/components/work/DepartmentManagers";

const EMPTY_ROLE = { name: "", department: "Sales / CRM", data_scope: "OWN", approval_level: 0, permissions: {} };

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState({ modules: [], actions: [], scopes: [] });
  const [departments, setDepartments] = useState([]);
  const [selected, setSelected] = useState(null);   // role doc being edited
  const [draft, setDraft] = useState(null);         // editable copy
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_ROLE);
  const [userCounts, setUserCounts] = useState({});

  const load = useCallback(async () => {
    const [r, m, d, u] = await Promise.all([
      api.get("/rbac/roles"), api.get("/rbac/meta"), api.get("/rbac/departments"), api.get("/users"),
    ]);
    setRoles(r.data);
    setMeta(m.data);
    setDepartments(d.data);
    const counts = {};
    u.data.forEach((x) => { if (x.status === "active") counts[x.role] = (counts[x.role] || 0) + 1; });
    setUserCounts(counts);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Failed to load roles")); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    roles.forEach((r) => { (g[r.department] = g[r.department] || []).push(r); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [roles]);

  const selectRole = (r) => {
    setSelected(r);
    setDraft({ name: r.name, department: r.department, data_scope: r.data_scope || "OWN",
               approval_level: r.approval_level || 0, permissions: JSON.parse(JSON.stringify(r.permissions || {})) });
  };

  const togglePerm = (module, action) => {
    const perms = { ...draft.permissions };
    const cur = new Set(perms[module] || []);
    if (cur.has(action)) cur.delete(action); else cur.add(action);
    perms[module] = [...cur];
    setDraft({ ...draft, permissions: perms });
  };

  const save = async () => {
    try {
      await api.put(`/rbac/roles/${selected.role_id}`, draft);
      toast.success(`Role ${selected.role_id} saved — effective immediately for all its users`);
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed"); }
  };

  const duplicate = async () => {
    try {
      const r = await api.post(`/rbac/roles/${selected.role_id}/duplicate`);
      toast.success(`Duplicated as ${r.data.role_id}`);
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Duplicate failed"); }
  };

  const toggleStatus = async () => {
    const next = selected.status === "active" ? "inactive" : "active";
    try {
      const r = await api.patch(`/rbac/roles/${selected.role_id}/status`, { status: next });
      toast.success(`Role ${next === "active" ? "reactivated" : "deactivated"}`);
      await load();
      setSelected(r.data);
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
  };

  const createRole = async () => {
    try {
      const r = await api.post("/rbac/roles", createForm);
      toast.success(`Role ${r.data.role_id} created — select it to set permissions`);
      setCreateOpen(false);
      setCreateForm(EMPTY_ROLE);
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Create failed"); }
  };

  return (
    <div className="space-y-4" data-testid="roles-page">
      <SectionTitle right={
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="role-create-button"><Plus className="w-4 h-4 mr-1" /> New Role</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border" data-testid="role-create-dialog">
            <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Create Role</DialogTitle><DialogDescription className="sr-only">Create a new custom role</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>Role name</Label><Input data-testid="role-name-input" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. Export Coordinator" /></div>
              <div><Label>Department</Label>
                <Select value={createForm.department} onValueChange={(v) => setCreateForm({ ...createForm, department: v })}>
                  <SelectTrigger data-testid="role-dept-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.code} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data scope</Label>
                  <Select value={createForm.data_scope} onValueChange={(v) => setCreateForm({ ...createForm, data_scope: v })}>
                    <SelectTrigger data-testid="role-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.scopes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Approval level</Label><Input type="number" data-testid="role-approval-input" value={createForm.approval_level} onChange={(e) => setCreateForm({ ...createForm, approval_level: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={createRole} data-testid="role-create-submit">Create Role</Button>
            </div>
          </DialogContent>
        </Dialog>
      }>Role Management & Permission Matrix</SectionTitle>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg overflow-y-auto max-h-[70vh]" data-testid="roles-list">
          {grouped.map(([dept, rs]) => (
            <div key={dept}>
              <p className="px-4 pt-3 pb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{dept}</p>
              {rs.map((r) => (
                <button key={r.role_id} onClick={() => selectRole(r)} data-testid={`role-row-${r.role_id}`}
                  className={`w-full text-left px-4 py-2 border-b border-border/40 hover:bg-muted/40 transition-colors ${selected?.role_id === r.role_id ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{r.name}</span>
                    <span className="flex items-center gap-1.5">
                      {userCounts[r.role_id] > 0 && <span className="text-[9px] font-mono text-muted-foreground">{userCounts[r.role_id]}u</span>}
                      {r.is_legacy && <span className="text-[9px] font-mono uppercase text-signal-blue">core</span>}
                      {r.status !== "active" && <span className="text-[9px] font-mono uppercase text-signal-red">inactive</span>}
                    </span>
                  </div>
                  <p className="text-[10px] font-mono text-muted-foreground">{r.role_id} · scope {r.data_scope || "OWN"} · L{r.approval_level ?? 0}</p>
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4" data-testid="role-editor">
          {!draft ? (
            <p className="text-sm text-muted-foreground py-10 text-center">Select a role to view and edit its permission matrix.</p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-heading text-lg uppercase">{selected.name} <span className="font-mono text-xs text-muted-foreground">({selected.role_id})</span></h3>
                  <p className="text-xs text-muted-foreground">{selected.is_legacy ? "Core system role — permission changes apply to all users with this role" : "Custom role"}</p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={duplicate} data-testid="role-duplicate-button"><Copy className="w-3.5 h-3.5 mr-1" /> Duplicate</Button>
                  {selected.role_id !== "admin" && (
                    <Button size="sm" variant="outline" onClick={toggleStatus} data-testid="role-status-button"
                      className={selected.status === "active" ? "text-signal-red border-signal-red/40" : "text-signal-green border-signal-green/40"}>
                      {selected.status === "active" ? <><Ban className="w-3.5 h-3.5 mr-1" /> Deactivate</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Reactivate</>}
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Department</Label>
                  <Select value={draft.department} onValueChange={(v) => setDraft({ ...draft, department: v })}>
                    <SelectTrigger data-testid="edit-role-dept"><SelectValue /></SelectTrigger>
                    <SelectContent>{departments.map((d) => <SelectItem key={d.code} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Data scope</Label>
                  <Select value={draft.data_scope} onValueChange={(v) => setDraft({ ...draft, data_scope: v })}>
                    <SelectTrigger data-testid="edit-role-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.scopes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Approval level</Label><Input type="number" data-testid="edit-role-approval" value={draft.approval_level} onChange={(e) => setDraft({ ...draft, approval_level: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <div className="border border-border rounded-lg overflow-x-auto max-h-[46vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="permission-matrix">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Module</th>
                      {meta.actions.map((a) => <th key={a} className="px-1 py-2 text-[9px] font-mono uppercase tracking-widest text-muted-foreground text-center">{a.slice(0, 4)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {meta.modules.map((m) => (
                      <tr key={m} className="border-b border-border/40">
                        <td className="px-3 py-1.5 font-mono text-xs">{m}</td>
                        {meta.actions.map((a) => {
                          const on = (draft.permissions[m] || []).includes(a);
                          return (
                            <td key={a} className="px-1 py-1.5 text-center">
                              <button type="button" onClick={() => togglePerm(m, a)} data-testid={`matrix-${m}-${a}`}
                                className={`w-4 h-4 rounded-sm border transition-colors ${on ? "bg-primary border-primary" : "border-border hover:border-muted-foreground"}`} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={save} data-testid="role-save-button">Save Role Permissions</Button>
            </div>
          )}
        </div>
      </div>
      <DepartmentManagers departments={departments} />
    </div>
  );
}
